import { useMemo, useRef, useState } from 'react';
import { Input } from '@nextui-org/react';

type FreeSoloOption = {
  value: string;
  displayValue?: string;
  count?: number;
  isOfficial?: boolean;
};

interface FreeSoloSelectProps {
  label: string;
  placeholder?: string;
  value: string;
  onValueChange: (value: string) => void;
  options: FreeSoloOption[];
  isDisabled?: boolean;
}

const FreeSoloSelect = ({
  label,
  placeholder,
  value,
  onValueChange,
  options,
  isDisabled = false,
}: FreeSoloSelectProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const filteredOptions = useMemo(() => {
    const query = value.trim().toUpperCase();
    if (!query) {
      return options;
    }
    return options.filter((option) =>
      option.value.toUpperCase().includes(query)
    );
  }, [options, value]);

  return (
    <div className='relative'>
      <Input
        ref={inputRef}
        label={label}
        placeholder={placeholder}
        value={value}
        onChange={(event) => {
          const nextValue = event.target.value;
          const cursorPosition = event.target.selectionStart;
          onValueChange(nextValue || '');
          if (!isOpen) setIsOpen(true);
          requestAnimationFrame(() => {
            if (!inputRef.current) return;
            const nextPosition =
              cursorPosition ?? inputRef.current.value.length;
            try {
              inputRef.current.setSelectionRange(nextPosition, nextPosition);
            } catch {
              // Ignore selection errors in unsupported inputs.
            }
          });
        }}
        onFocus={() => setIsOpen(true)}
        onBlur={() => {
          setTimeout(() => setIsOpen(false), 150);
        }}
        isDisabled={isDisabled}
      />
      {isOpen && !isDisabled && filteredOptions.length > 0 && (
        <div className='absolute z-50 mt-1 w-full rounded-medium border border-default-200 bg-background shadow-lg max-h-64 overflow-auto'>
          {filteredOptions.map((option) => {
            const displayValue = (option.displayValue ?? option.value)
              .replace(/^ +/, (spaces) => '_'.repeat(spaces.length))
              .replace(/ +$/, (spaces) => '_'.repeat(spaces.length));
            const keySuffix = option.isOfficial
              ? 'official'
              : option.count ?? 'plain';
            return (
              <button
                key={`${option.value}-${keySuffix}`}
                type='button'
                className='w-full px-3 py-2 text-left text-sm text-foreground hover:bg-default-100 focus:bg-default-100 focus:outline-none'
                onMouseDown={(event) => {
                  event.preventDefault();
                  onValueChange(option.value);
                  setIsOpen(false);
                }}
              >
                <div className='flex justify-between items-center w-full'>
                  <span className='font-mono'>{displayValue}</span>
                  {option.isOfficial ? (
                    <span className='text-xs font-medium text-emerald-700 border border-emerald-200 px-2 py-0.5 rounded-full'>
                      Oficial
                    </span>
                  ) : option.count !== undefined ? (
                    <span className='text-sm text-default-400 ml-2'>
                      ({option.count.toLocaleString()})
                    </span>
                  ) : null}
                </div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default FreeSoloSelect;
